import { useCallback, useEffect, useRef, useState } from 'react';
import { applyMyKeys, clearMyScenarioKeys, getUser } from '../services/UserServices';
import {
    KEY_STORES,
    applyKeyChanges as computeKeyChanges,
    clearLocalScenarioKeys,
    keyStoreFor,
    normalizeKeyIds,
    readLocalKeys,
    saveLocalKeyChanges,
} from '../utils/playerKeys';

/**
 * The keys a player holds, for one scenario being played or previewed.
 *
 * The one place play reads and changes keys, so preview, logged-out and
 * signed-in play cannot drift apart again. See utils/playerKeys.js for where
 * each kind of play keeps them.
 *
 * A signed-in player's keys are the server's to decide: the hook tells it which
 * published situation was reached or which choice was picked (`source`), never
 * which keys to add, and adopts the keys it answers with. The local change is
 * shown straight away so play does not wait on the network.
 *
 * @param {{scenarioId?: string, userId?: string|null, preview?: boolean}} options
 * @returns {{
 *   userKeys: string[],
 *   keysLoaded: boolean,
 *   applyKeyChanges: (changes: {
 *     grants?: Array,
 *     removes?: Array,
 *     source?: {situationId: string}|{choiceId: string},
 *   }) => Promise<void>,
 *   clearScenarioKeys: () => Promise<void>,
 *   lastChange: {id: number, gained: string[], lost: string[]}|null,
 * }}
 */
export default function usePlayerKeys({ scenarioId, userId, preview }) {
    const store = keyStoreFor({ preview: !!preview, userId });

    const [userKeys, setUserKeys] = useState(() => readLocalKeys(store, scenarioId));
    const [keysLoaded, setKeysLoaded] = useState(store !== KEY_STORES.ACCOUNT);
    // Only changes made by play: clearing keys on restart is not news.
    const [lastChange, setLastChange] = useState(null);

    // Changes can arrive back to back (an arrival, then a click), so each one
    // builds on the latest keys rather than on the last render's.
    const keysRef = useRef(userKeys);
    const changeSeq = useRef(0);

    // Account requests run one at a time, in order, and only the newest one's
    // answer is adopted — an older answer would undo a newer local change.
    const accountQueue = useRef(Promise.resolve());
    const requestSeq = useRef(0);

    const setKeys = useCallback((next) => {
        keysRef.current = next;
        setUserKeys(next);
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLastChange(null);

        if (store !== KEY_STORES.ACCOUNT) {
            setKeys(readLocalKeys(store, scenarioId));
            setKeysLoaded(true);
            return undefined;
        }

        setKeysLoaded(false);
        setKeys([]);
        getUser(userId)
            .then((user) => {
                if (!cancelled) setKeys(normalizeKeyIds(user?.keys));
            })
            .catch((error) => {
                console.error('Error loading keys:', error);
            })
            .finally(() => {
                if (!cancelled) setKeysLoaded(true);
            });

        return () => {
            cancelled = true;
        };
    }, [store, scenarioId, userId, setKeys]);

    /** Queue an account request; adopt its keys if nothing newer was queued since. */
    const runAccountRequest = useCallback(
        (request) => {
            requestSeq.current += 1;
            const seq = requestSeq.current;
            const run = accountQueue.current.then(async () => {
                try {
                    const response = await request();
                    if (seq === requestSeq.current && Array.isArray(response?.keys)) {
                        setKeys(normalizeKeyIds(response.keys));
                    }
                } catch (error) {
                    console.error('Error saving key changes:', error);
                    // The server refused or failed, so the local guess may be
                    // wrong. Take its word for what is held.
                    if (seq === requestSeq.current) {
                        try {
                            const user = await getUser(userId);
                            if (seq === requestSeq.current) setKeys(normalizeKeyIds(user?.keys));
                        } catch (reloadError) {
                            console.error('Error reloading keys:', reloadError);
                        }
                    }
                }
            });
            accountQueue.current = run;
            return run;
        },
        [userId, setKeys]
    );

    const applyKeyChanges = useCallback(
        async ({ grants, removes, source } = {}) => {
            const { next, gained, lost } = computeKeyChanges(keysRef.current, { grants, removes });
            if (gained.length === 0 && lost.length === 0) return;

            setKeys(next);
            changeSeq.current += 1;
            setLastChange({ id: changeSeq.current, gained, lost });

            if (store !== KEY_STORES.ACCOUNT) {
                saveLocalKeyChanges(store, scenarioId, { gained, lost });
                return;
            }

            if (!source || !(source.situationId || source.choiceId)) {
                console.error('A signed-in key change needs the situation or choice that caused it.');
                return;
            }
            const body = source.situationId
                ? { situationId: String(source.situationId) }
                : { choiceId: String(source.choiceId) };
            await runAccountRequest(() => applyMyKeys(body));
        },
        [store, scenarioId, setKeys, runAccountRequest]
    );

    const clearScenarioKeys = useCallback(async () => {
        if (store !== KEY_STORES.ACCOUNT) {
            setKeys(clearLocalScenarioKeys(store, scenarioId));
            return;
        }
        if (!scenarioId) return;
        await runAccountRequest(() => clearMyScenarioKeys(scenarioId));
    }, [store, scenarioId, setKeys, runAccountRequest]);

    return { userKeys, keysLoaded, applyKeyChanges, clearScenarioKeys, lastChange };
}
